/**
 * Shared basemap providers for live employee location.
 * Google is preferred when REACT_APP_GOOGLE_MAPS_API_KEY is set.
 * All other layers work without a key.
 */

const mapTilerKey = process.env.REACT_APP_MAPTILER_KEY || '';
export const GOOGLE_MAPS_API_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();
export const hasGoogleMapsKey = Boolean(GOOGLE_MAPS_API_KEY);

export const MAP_PROVIDERS = {
  google: {
    id: 'google',
    label: 'Google',
    engine: 'google',
    requiresKey: true,
  },
  osm: {
    id: 'osm',
    label: 'OSM',
    engine: 'leaflet',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    fallbackUrl: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    maxNativeZoom: 19,
    maxZoom: 22,
  },
  carto: {
    id: 'carto',
    label: 'Carto',
    engine: 'leaflet',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    fallbackUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    maxNativeZoom: 20,
    maxZoom: 22,
  },
  topo: {
    id: 'topo',
    label: 'Topo',
    engine: 'leaflet',
    attribution:
      'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    fallbackUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    maxNativeZoom: 17,
    maxZoom: 22,
  },
  esri: {
    id: 'esri',
    label: 'Esri',
    engine: 'leaflet',
    attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    fallbackUrl:
      'https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    maxNativeZoom: 19,
    maxZoom: 22,
  },
  satellite: {
    id: 'satellite',
    label: 'Satellite',
    engine: 'leaflet',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    fallbackUrl:
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    maxNativeZoom: 19,
    maxZoom: 22,
  },
  hybrid: {
    id: 'hybrid',
    label: 'Hybrid',
    engine: 'leaflet',
    attribution:
      'Tiles &copy; Esri · Labels &copy; <a href="https://carto.com/attributions">CARTO</a>',
    url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    fallbackUrl:
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    overlayUrl:
      'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
    maxNativeZoom: 19,
    maxZoom: 22,
  },
  '3d': {
    id: '3d',
    label: '3D',
    engine: 'maplibre',
  },
};

/** Keep old ids working if something still references them. */
MAP_PROVIDERS.street = MAP_PROVIDERS.carto;
MAP_PROVIDERS.light = MAP_PROVIDERS.carto;

export const getMapLibreStyle = () => {
  if (mapTilerKey) {
    return `https://api.maptiler.com/maps/streets-v2/style.json?key=${mapTilerKey}`;
  }
  return 'https://tiles.openfreemap.org/styles/liberty';
};

export const getMapLibreSatelliteStyle = () => ({
  version: 8,
  sources: {
    esri: {
      type: 'raster',
      tiles: [
        'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 19,
      attribution: 'Esri',
    },
    labels: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      maxzoom: 20,
      attribution: 'CARTO',
    },
  },
  layers: [
    { id: 'esri', type: 'raster', source: 'esri' },
    { id: 'labels', type: 'raster', source: 'labels', paint: { 'raster-opacity': 0.9 } },
  ],
});

/** Prefer Google when an API key is configured; otherwise OpenStreetMap. */
export const DEFAULT_MAP_VIEW = hasGoogleMapsKey ? 'google' : 'osm';
